import React, { useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { CloseOutline, Trash2Outline } from '@lyearn/icons';
import { Avatar, Button, IconButton, Stack, Typography, useSnackbar } from '@lyearn/ui';
import { Modal } from '@/components/Modals';

import { useUpdateTimeLogMutation } from '../../graphql/mutations/generated/updateTimeLog';
import { TimeLogFragment } from '../../graphql/queries/generated/timeLogs';
import { useTimer } from '../../hooks/useTimer';
import { IntervalView } from '../IntervalView';

type Props = { open: boolean; onClose: () => void; timeLog: TimeLogFragment | null };

const EntryEditModal = ({ open, onClose, timeLog }: Props) => {
  const { t } = useTranslation();
  const [instructions, setInstructions] = useState(timeLog?.instruction ?? '');

  const {
    startTime,
    endTime,
    rawTimeInput,
    setRawTimeInput,
    handleStartDateChange,
    handleEndDateChange,
    handleStartTimeSelect,
    handleEndTimeSelect,
  } = useTimer({
    initialStartTime: timeLog?.startTime ? new Date(timeLog.startTime) : undefined,
    initialEndTime: timeLog?.endTime ? new Date(timeLog.endTime) : undefined,
  });

  const [{ fetching: isUpdatingTimeLog }, updateTimeLog] = useUpdateTimeLogMutation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();

  const onDelete = () => {
    if (!timeLog?.id) {
      return;
    }
    updateTimeLog({
      data: {
        removeTimeLog: timeLog.id,
      },
    }).then((res) => {
      if (res?.data?.updateTimeLog?.success) {
        onClose();
        showSnackbar({
          message: 'Time Log Deleted',
        });
      } else {
        showErrorSnackbar({
          message: t('Common:Error.SomethingWentWrong'),
        });
      }
    });
  };

  const onSave = () => {
    if (!timeLog?.id) {
      return;
    }
    updateTimeLog({
      data: {
        editTimeLog: {
          timeLogId: timeLog.id,
          instruction: instructions,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
        },
      },
    }).then((res) => {
      if (res?.data?.updateTimeLog?.success) {
        onClose();
        showSnackbar({
          message: 'Time Log Updated',
        });
      } else {
        showErrorSnackbar({
          message: t('Common:Error.SomethingWentWrong'),
        });
      }
    });
  };

  if (!timeLog || !timeLog.user) {
    return null;
  }

  return (
    <Modal
      classes={{
        paper: '!w-[480px] !min-h-[320px]',
      }}
      open={open}
      renderHeader={() => (
        <Stack direction="col">
          <Stack alignItems="center" className="px-24 py-12" justifyContent="between">
            <Stack alignItems="center" gapX={8}>
              <Avatar src={timeLog?.user?.profilePicture ?? ''} />
              <Typography variant="body-long-01">{timeLog?.user?.name}</Typography>
            </Stack>
            <Stack gapX={8}>
              <IconButton onClick={onDelete}>
                <Trash2Outline fillColorToken="icon-error" />
              </IconButton>
              <IconButton onClick={onClose}>
                <CloseOutline />
              </IconButton>
            </Stack>
          </Stack>
          <Stack className="h-[1px] -mx-24 bg-border" />
        </Stack>
      )}
      showCloseBtn={false}
      showCloseIcon={false}
      onClose={onClose}>
      <Stack alignItems="center" className="px-24 pb-24" direction="col" justifyContent="end">
        <IntervalView
          endTime={endTime}
          fetching={false}
          handleEndDateChange={handleEndDateChange}
          handleEndTimeSelect={handleEndTimeSelect}
          handleStartDateChange={handleStartDateChange}
          handleStartTimeSelect={handleStartTimeSelect}
          instructions={instructions}
          isEditMode={true}
          rawTimeInput={rawTimeInput}
          selectedUserId={timeLog.userId}
          setInstructions={setInstructions}
          setRawTimeInput={setRawTimeInput}
          setSelectedUserId={() => {}}
          startTime={startTime}
          user={timeLog.user}
        />
        <Stack className="w-full mt-32 px-24" gapX={8} justifyContent="end">
          <Button fullWidth shape="square" variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={isUpdatingTimeLog}
            loading={isUpdatingTimeLog}
            shape="square"
            onClick={onSave}>
            Save
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default React.memo(EntryEditModal);
